import { Component, OnInit } from '@angular/core';
import { Supplier } from '../../classes/supplier';
import { SupplierService } from '../../services/supplier';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GeneralModalComponent } from '../../modals/general/general-modal/general-modal.component';
import { SupplierCreateModalComponent } from '../../modals/supplier/supplier-create-modal/supplier-create-modal.component';
import { AuthService } from '../../services/auth';
import { User } from '../../classes/user';
import { splitEmails } from '../../utils/supplierValidation';
import {
    AdvancedSearchField,
    AdvancedSearchState,
    countActiveRules,
    emptyAdvancedState
} from '../../utils/advancedSearch';
import { loadAdvancedState, saveAdvancedState } from '../../utils/filterStateStorage';
import { csvFileName, downloadCsv } from '../../utils/csvExport';

@Component({
    selector: 'app-supplier',
    templateUrl: './supplier.component.html',
    styleUrls: ['./supplier.component.css']
})
export class SupplierComponent implements OnInit {
    // Pagination Parameters
    paginationConfig = {
        itemsPerPage: 25,
        currentPage: 1
    };
    columnLayoutToken = 0;
    paginationParams = {
        autoHide: true,
        maxSize: 7,
        responsive: true
    };
    paginationLabels = {
        previousLabel: 'Precedente',
        nextLabel: 'Successiva',
        screenReaderPaginationLabel: 'Paginazione',
        screenReaderPageLabel: 'pagina',
        screenReaderCurrentLabel: `Sei sulla pagina`
    };

    // Filters
    globalSearchFilterValue: string = '';
    idFilterValue: string = '';
    nameFilterValue: string = '';
    cfFilterValue: string = '';
    emailFilterValue: string = '';

    // Ricerca Avanzata
    private readonly screenKey = 'fornitori';
    readonly advancedFields: AdvancedSearchField<Supplier>[] = [
        {key: 'id', label: 'ID Fornitore', type: 'number', value: s => s.IDSupplier},
        {key: 'name', label: 'Nome', type: 'text', value: s => s.Name},
        {key: 'cf', label: 'Codice Fiscale', type: 'text', value: s => s.CF},
        {key: 'email', label: 'Email', type: 'text', value: s => s.Email}
    ];
    advancedState: AdvancedSearchState = emptyAdvancedState();

    user: User = new User();
    suppliers: Array<Supplier> = [];

    constructor(private router: Router,
                private authService: AuthService,
                private modalService: NgbModal,
                private supplierService: SupplierService) {
    }

    ngOnInit(): void {
        this.advancedState = loadAdvancedState(this.screenKey, this.advancedFields) ?? emptyAdvancedState();
        this.authService.isUserSignedIn()
            .subscribe({
                next: () => {
                    this.authService.getUser()
                        .subscribe({
                            next: (user: any) => {
                                this.user = user;
                                if (this.user.FKIDUserRole !== 1) {
                                    this.router.navigate(['/dashboard']);
                                    return;
                                }
                                this.loadSuppliers();
                            },
                            error: () => {
                                this.router.navigate(['/login']);
                            }
                        });
                },
                error: () => {
                    this.router.navigate(['/login']);
                }
            });
    }

    private loadSuppliers(): void {
        this.supplierService.getAll()
            .subscribe({
                next: (suppliers: Array<Supplier>) => {
                    this.suppliers = suppliers;
                    this.relockColumns();
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    pageChanged(event: any) {
        this.paginationConfig.currentPage = event;
        this.relockColumns();
    }

    private relockColumns(): void {
        this.columnLayoutToken++;
    }

    getEmails(emailValue: string): string[] {
        return splitEmails(emailValue);
    }

    onFilterChange(): void {
        this.paginationConfig.currentPage = 1;
    }

    clearFilters(): void {
        this.globalSearchFilterValue = '';
        this.idFilterValue = '';
        this.nameFilterValue = '';
        this.cfFilterValue = '';
        this.emailFilterValue = '';
        this.advancedState = emptyAdvancedState();
        saveAdvancedState(this.screenKey, this.advancedState, this.advancedFields);
        this.paginationConfig.currentPage = 1;
    }

    onAdvancedStateChange(state: AdvancedSearchState): void {
        this.advancedState = state;
        saveAdvancedState(this.screenKey, state, this.advancedFields);
        this.paginationConfig.currentPage = 1;
    }

    exportCsv(suppliers: Array<Supplier>): void {
        downloadCsv(
            csvFileName('fornitori'),
            ['ID Fornitore', 'Nome', 'Codice Fiscale', 'Email'],
            suppliers.map(supplier => [
                supplier.IDSupplier ?? '',
                supplier.Name || '',
                supplier.CF || '',
                this.getEmails(supplier.Email).join(', ')
            ])
        );
    }

    hasActiveFilters(): boolean {
        return !!(
            this.globalSearchFilterValue
            || this.idFilterValue
            || this.nameFilterValue
            || this.cfFilterValue
            || this.emailFilterValue
            || countActiveRules(this.advancedState, this.advancedFields) > 0
        );
    }

    goToSupplierDetail(idSupplier: number | undefined) {
        if (idSupplier == null) {
            return;
        }
        this.router.navigate(['/fornitori/' + idSupplier]);
    }

    openCreateSupplierModal() {
        const modalRef = this.modalService.open(SupplierCreateModalComponent, { size: 'xl', centered: true });
        modalRef.result.then((supplierBack: Supplier) => {
            if (supplierBack?.IDSupplier !== undefined) {
                this.loadSuppliers();
            }
        }, () => {});
    }

    openDeleteSupplierModal(supplier: Supplier) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Cancellazione <strong>Fornitore</strong>';
        modalRef.componentInstance.text = `Confermi la cancellazione del fornitore "${supplier.Name}"?`;
        modalRef.result.then(() => {
            this.supplierService.delete(supplier.IDSupplier)
                .subscribe({
                    next: () => {
                        this.suppliers = this.suppliers.filter((currentSupplier: Supplier) => currentSupplier.IDSupplier !== supplier.IDSupplier);
                    },
                    error: (err: HttpErrorResponse) => {
                        const message = err.error && err.error.message
                            ? err.error.message
                            : 'Non è stato possibile eliminare il fornitore.';
                        const errorModal = this.modalService.open(GeneralModalComponent);
                        errorModal.componentInstance.title = 'Eliminazione <strong>Fornitore</strong>';
                        errorModal.componentInstance.text = message;
                    }
                });
        }, () => {});
    }

}
